import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DojoCodeStepProps {
  dojoCode: string;
  setDojoCode: (code: string) => void;
  isValid: boolean;
  setIsValid: (valid: boolean) => void;
  dojoName: string;
  setDojoName: (name: string) => void;
  error: string;
  setError: (error: string) => void;
}

const DojoCodeStep: React.FC<DojoCodeStepProps> = ({
  dojoCode,
  setDojoCode,
  isValid,
  setIsValid,
  dojoName,
  setDojoName,
  error,
  setError
}) => {
  useUiLanguage();
  const [isValidating, setIsValidating] = useState(false);


  const validateDojoCode = async (code: string) => {
    if (!code.trim()) {
      setIsValid(false);
      setError('');
      setDojoName('');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('validate_dojo_code', {
        submitted_code: code.trim()
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError(uiText("Unable to validate code. Please try again."));
        setIsValid(false);
        setDojoName('');
        return;
      }

      const result = data as { valid: boolean; error?: string; dojo_name?: string };

      if (result.valid) {
        setIsValid(true);
        setDojoName(result.dojo_name || '');
        setError('');
      } else {
        setIsValid(false);
        setDojoName('');
        setError(result.error || 'Invalid dojo code');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setError(uiText("Unable to validate code. Please try again."));
      setIsValid(false);
      setDojoName('');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCodeChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setDojoCode(upperValue);

    // Reset validation state when code changes
    setIsValid(false);
    setError('');
    setDojoName('');
  };

  const handleValidate = () => {
    validateDojoCode(dojoCode);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {uiText("Join a Dojo")}</h3>
        <p className="text-sm text-gray-600">
          {uiText("Enter your dojo's join code to register.")}{' '}<strong>{uiText("This is required for all student registrations.")}</strong>
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="dojoCode">{uiText("Dojo Code")}</Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="dojoCode"
              type="text"
              placeholder={uiText("Enter dojo code")}
              value={dojoCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={isValidating}
              className={`
                ${isValid ? 'border-green-500 focus:border-green-500' : ''}
                ${error ? 'border-red-500 focus:border-red-500' : ''}
              `}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleValidate}
              disabled={!dojoCode.trim() || isValidating}
              className="px-4"
            >
              {isValidating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                uiText("Validate")
              )}
            </Button>
          </div>
        </div>

        {/* Validation Status */}
        {isValid && dojoName && (
          <div className="flex items-center gap-2 text-green-600 text-sm">
            <CheckCircle className="h-4 w-4" />
            <span>{uiText("Valid! You'll join:")}{' '}<strong>{dojoName}</strong></span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DojoCodeStep;